/**
 * Onde achar uma gravação livre da faixa que está tocando, para tocar dentro
 * do app.
 *
 * O Jukebox sabia dizer o que a outra pessoa está ouvindo e mandava para fora,
 * no YouTube Music. Isto aqui é o outro caminho: quando existe uma gravação
 * livre da mesma faixa, ela toca aqui mesmo.
 *
 * Fala com o mesmo Internet Archive dos audiolivros e reaproveita as peças de
 * lá -- os endereços, o `buscarJson` e o `faixasDeArquivos`, que já sabe
 * escolher um formato só e ler `track: "3/15"`.
 */

import { BUSCA_IA, IMG_IA, META_IA, buscarJson, faixasDeArquivos } from '../livros/acervos';
import { achatado, faixaPorNome, faixaProcurada, mesmoArtista } from './casar';
import type { Faixa } from '../livros/audiolivro';

/**
 * De quais coleções do acervo uma gravação pode sair.
 *
 * Esta lista é o que separa uma gravação livre de um arquivo qualquer que
 * alguém subiu, e foi medida contra o acervo de verdade. Sem ela, procurar
 * `mediatype:audio` responde a tudo e responde errado:
 *
 *   Beatles / "Yesterday"       -> `Yesterday Always Remembers`, que não é a
 *                                  música, e um vinil inteiro varrido
 *   The Weeknd / "Blinding..."  -> um remix de DJ com outro nome
 *
 * Nenhum dos dois é a gravação, e nenhum dos dois é livre. Tocar isso como se
 * fosse a faixa seria pior do que não oferecer nada. Com a lista, os dois
 * respondem zero -- e Satie, Joplin e Debussy continuam respondendo a
 * gravação certa.
 *
 *   georgeblood, 78rpms  o Great 78 Project: 78 rotações varridos, a era que
 *                        já caiu em domínio público
 *   musopen              clássico em domínio público, gravado para isso
 *   netlabels            selos que publicam em Creative Commons
 *
 * O `etree` -- shows que a própria banda deixa gravar e circular -- não entra
 * nesta lista porque não se procura do mesmo jeito. Ele tem passada própria,
 * logo abaixo.
 */
const COLECOES_LIVRES = ['georgeblood', '78rpms', 'musopen', 'netlabels'];

const CLAUSULA_LIVRE = `(${COLECOES_LIVRES.map((c) => `collection:(${c})`).join(' OR ')})`;

/**
 * A segunda passada: o `etree`.
 *
 * Aqui o item é o show inteiro e o título dele é a data e a casa --
 * `1977-05-09 Buffalo, NY` --, então `title:("Sugar Magnolia")` nunca casa. Era
 * esse o motivo de o `etree` ter ficado de fora. O que destrava é o campo que
 * ninguém olha: **a `description` do item é o repertório**, e o acervo a
 * indexa. Medido contra o acervo de verdade:
 *
 *   Grateful Dead / `Sugar Magnolia`   18381 shows -> 5180 com a música
 *   Phish / `You Enjoy Myself`           153 shows ->   18
 *   Smashing Pumpkins / `Tonight...`    1639 shows ->  936
 *
 * E o que decide não é o total, é quantos dos três primeiros têm mesmo o
 * arquivo: **12 de 12 com a `description`, 4 de 12 sem ela**. O medo antigo
 * ("varrer a lista de arquivos de todo show da banda") estava certo a respeito
 * de procurar só pelo artista, e é exatamente o que esta cláusula evita.
 *
 * Banda que não deixa gravar responde zero -- Pearl Jam, Dave Matthews Band,
 * Wilco e Los Hermanos deram `total=0`. Ausência não inventa resposta.
 *
 * Custa os mesmos três pedidos de sempre, porque o `varrer` já abria candidato
 * e olhava a lista de arquivos. O que muda é quem entra na lista.
 */
const CLAUSULA_ETREE = 'collection:(etree)';

/** Quantos itens da busca abrir antes de desistir. Cada um custa um pedido. */
const QUANTOS_CANDIDATOS = 3;

/** Uma gravação livre, pronta para tocar. */
export interface Gravacao {
  /** O item no Internet Archive de onde a faixa saiu. */
  iaId: string;
  /**
   * O item inteiro, e não só a faixa procurada.
   *
   * Parece desperdício e é o contrário: o `varrer` já pede a lista de arquivos
   * do item para achar a faixa, então a lista inteira já está paga. Guardá-la
   * custa zero pedidos a mais e é o que deixa o tocador seguir para a próxima
   * -- um show do `etree` tem vinte músicas e uma coletânea de netlabel tem
   * quinze. Antes disto, as outras dezenove eram jogadas fora.
   */
  faixas: Faixa[];
  /**
   * Onde na lista está a faixa que foi procurada.
   *
   * Nunca muda depois de montada: é "a que a pessoa pediu", não "a que está
   * tocando agora". Quem anda pelo disco é o tocador, que conta à parte. É
   * isto que deixa o nome do Last.fm voltar quando se volta para ela.
   */
  indice: number;
  /** Como o acervo chama o item: o disco, o 78, a coletânea. */
  album: string;
  /** Quem o acervo diz que gravou. Nem sempre é o artista da faixa. */
  autoria: string;
  capaUrl: string;
}

/**
 * Uma faixa em cartaz: a gravação que toca e os nomes que se mostram.
 *
 * Mora aqui, e não no tocador de cada app, porque são dois tocadores -- o
 * `<audio>` do site e o do `expo-video` no celular -- e a faixa em cartaz é a
 * mesma coisa nos dois. Tê-la duas vezes seria ter dois lugares para corrigir.
 */
export interface EmCartaz {
  gravacao: Gravacao;
  /**
   * O nome da faixa e de quem a fez vêm do Last.fm, não do acervo: é o que a
   * pessoa reconhece. O acervo costuma escrever `GYMNOPÉDIE No. 1 - Boston
   * Symphony Orchestra`, com a orquestra no meio do título.
   */
  titulo: string;
  artista: string;
  /** A capa do Last.fm. Vazia quando não veio; a barra desenha a nota. */
  capaUrl: string;
}

/**
 * O que o acervo trata como operador. Vira espaço, porque nome de música não é
 * linguagem de consulta -- e `Bang!`, `(I Can't Get No)` ou `AC/DC` passando
 * cru não devolvem zero, devolvem erro de análise.
 *
 * Mesma ideia do `alvoDoTermo` dos livros, mas a cláusula aqui é outra: lá é
 * `title OU creator`, porque quem digita pode estar procurando pelo autor;
 * aqui é `title E creator`, porque os dois já vieram do Last.fm e afrouxar
 * para OU é justamente o que traz disco errado de artista certo.
 */
const OPERADOR = /[+\-!(){}[\]^"~*?:\\/&|]+/g;

function limpo(termo: string): string {
  return termo
    .replace(OPERADOR, ' ')
    .replace(/\b(?:AND|OR|NOT)\b/g, (p) => p.toLowerCase())
    .replace(/\s+/g, ' ')
    .trim();
}

interface RespostaDaBusca {
  response?: { docs?: Record<string, unknown>[] };
}

interface RespostaDoItem {
  files?: unknown[];
}

/**
 * A memória do que já se perguntou, inclusive as respostas vazias.
 *
 * Mesma razão do memo de capas do `jukebox.ts`: a tela pergunta de novo a cada
 * volta do relógio, e a faixa que o acervo não tem é a que mais se repete --
 * sem guardar o `null`, cada volta custaria de novo os pedidos todos dela.
 */
const lembradas = new Map<string, Gravacao | null>();

function chave(artista: string, faixa: string): string {
  return `${achatado(artista)}::${achatado(faixa)}`;
}

/**
 * Procura uma gravação livre da faixa. `null` quando não existe -- e "não
 * existe" é a resposta comum, não um defeito: quase toda música que se ouve
 * hoje tem dono, e o acervo livre não a tem.
 */
export async function procurarGravacao(artista: string, faixa: string): Promise<Gravacao | null> {
  const nome = limpo(faixa);
  const quem = limpo(artista);
  if (!nome || !quem) return null;

  const k = chave(quem, nome);
  const guardada = lembradas.get(k);
  if (guardada !== undefined) return guardada;

  const achada = await procurar(quem, nome);
  lembradas.set(k, achada);
  return achada;
}

/**
 * As duas passadas, nesta ordem.
 *
 * O estúdio antes do show de propósito: quando as duas têm a faixa, a gravação
 * de disco é a que a pessoa reconhece. A do `etree` só custa pedido quando a
 * primeira não achou nada -- e, quando nenhuma acha, o `null` fica guardado e
 * não se paga de novo.
 */
async function procurar(quem: string, nome: string): Promise<Gravacao | null> {
  return (
    (await varrer(
      `title:("${nome}") AND creator:("${quem}") AND mediatype:audio AND ${CLAUSULA_LIVRE}`,
      nome,
      '',
    )) ??
    (await varrer(
      `${CLAUSULA_ETREE} AND creator:("${quem}") AND description:("${nome}")`,
      nome,
      quem,
    ))
  );
}

/**
 * Abre os candidatos de uma busca até um deles ter mesmo a faixa.
 *
 * `artistaExato` vazio quer dizer "a busca já casou o título do item". Quando
 * vem preenchido é o contrário, e as duas consequências andam juntas: sem
 * `title:` na consulta, é aqui que se confere o artista (o acervo devolve banda
 * cover para `creator:("Phish")`) e é aqui que o nome da faixa tem de casar de
 * verdade, sem o atalho da faixa única.
 *
 * A conferência do artista vem antes de abrir o item: candidato recusado não
 * custa pedido.
 */
async function varrer(q: string, nome: string, artistaExato: string): Promise<Gravacao | null> {
  const p = new URLSearchParams({
    q,
    'fl[]': 'identifier,title,creator',
    'sort[]': 'downloads desc',
    rows: String(QUANTOS_CANDIDATOS),
    page: '1',
    output: 'json',
  });
  const busca = await buscarJson<RespostaDaBusca>(`${BUSCA_IA}?${p.toString()}`);
  const docs = busca?.response?.docs ?? [];

  for (const doc of docs) {
    const iaId = String(doc.identifier ?? '').trim();
    if (!iaId) continue;
    const autoria = primeiro(doc.creator);
    if (artistaExato && !mesmoArtista(autoria, artistaExato)) continue;
    const item = await buscarJson<RespostaDoItem>(`${META_IA}/${iaId}`);
    if (!item) continue;
    const faixas = faixasDeArquivos(iaId, item.files ?? []);
    const achada = artistaExato ? faixaPorNome(faixas, nome) : faixaProcurada(faixas, nome);
    if (!achada) continue;
    return {
      iaId,
      faixas,
      /* As duas escolhas devolvem um *elemento* da lista, nunca uma cópia, e é
         só por isso que o `indexOf` acha. Tem prova no `gravacao.mjs`, porque
         o dia em que alguma delas montar objeto novo o índice vira -1 calado. */
      indice: faixas.indexOf(achada),
      album: String(doc.title ?? '').trim() || iaId,
      autoria,
      capaUrl: `${IMG_IA}/${iaId}`,
    };
  }
  return null;
}

/** O acervo escreve `creator` como texto ou como lista, conforme o item. */
function primeiro(bruto: unknown): string {
  if (Array.isArray(bruto)) return String(bruto[0] ?? '').trim();
  return String(bruto ?? '').trim();
}
